<template>
  <public-layout>
    <div class="signin__header">
      <h1 v-bg-typography="'h1'">Welcome to Brainpeek</h1>
      <p v-bg-typography="'m-body'">a Braingineers product</p>
    </div>
    <transition name="fade">
      <div v-if="!signupValidationScreen">
        <div class="forms-container">
          <form v-on:submit.prevent="postSignin" class="signin">
            <h2 v-bg-typography="'h3'">Sign in</h2>
            <p
              v-if="signinError"
              class="signin-error"
              v-bg-typography="'s-body'"
            >
              {{ signinError }}
            </p>
            <ValidationProvider
              ref="signin_email"
              rules="required|email"
              v-slot="{ errors, valid, changed }"
            >
              <bg-field
                label="Email address"
                :error="errors && errors[0]"
                name="signin_email"
              >
                <bg-input
                  :error="errors && errors[0]"
                  name="signin_email"
                  v-model="signin.email"
                />
              </bg-field>
            </ValidationProvider>
            <ValidationProvider
              ref="signin_password"
              rules="required"
              v-slot="{ errors, valid, changed }"
            >
              <bg-field
                :error="errors && errors[0]"
                label="Password"
                name="signin_password"
              >
                <bg-input
                  :error="errors && errors[0]"
                  type="password"
                  name="signin_password"
                  v-model="signin.password"
                />
              </bg-field>
            </ValidationProvider>
            <div class="action-container">
              <bg-button
                :isLoading="isSigninLoading"
                class="signin-button"
                text="Sign in"
                @click="postSignin"
                type="submit"
                ref="signin"
              />
              <router-link class="forgot-password" to="/forgot-password">
                <p v-bg-typography="'link'">Forgot your password?</p>
              </router-link>
            </div>
          </form>
          <form v-on:submit.prevent="postSignup" class="signin">
            <h2 v-bg-typography="'h3'">Sign up</h2>
            <p class="signup-help" v-bg-typography="'s-body'">
              Create your account and we’ll send you a confirmation email. Open
              the email and click the link to verify your address. Once
              confirmed, you can sign in and start using Brainpeek.
            </p>
            <div class="names">
              <ValidationProvider
                ref="first_name"
                rules="required"
                v-slot="{ errors, valid, changed }"
              >
                <bg-field
                  :error="errors && errors[0]"
                  class="first_name"
                  label="First name"
                  name="first_name"
                >
                  <bg-input
                    v-model="signup.profile.first_name"
                    :error="errors && errors[0]"
                    :valid="changed && valid"
                  />
                </bg-field>
              </ValidationProvider>
              <ValidationProvider
                rules="required"
                ref="last_name"
                v-slot="{ errors, valid, changed }"
              >
                <bg-field
                  :error="errors && errors[0]"
                  class="last_name"
                  label="Last name"
                  name="last_name"
                >
                  <bg-input
                    v-model="signup.profile.last_name"
                    :error="errors && errors[0]"
                    :valid="changed && valid"
                  />
                </bg-field>
              </ValidationProvider>
            </div>
            <ValidationProvider
              ref="email"
              rules="required|email"
              v-slot="{ errors, valid, changed }"
            >
              <bg-field
                label="Email address"
                name="email"
                :error="errors && errors[0]"
              >
                <bg-input
                  v-model="signup.email"
                  :error="errors && errors[0]"
                  :valid="changed && valid"
                />
              </bg-field>
            </ValidationProvider>
            <ValidationProvider
              rules="required|min:6"
              vid="confirmation"
              v-slot="{ errors, valid, changed }"
            >
              <bg-field
                label="Password"
                :error="errors && errors[0]"
                name="password"
              >
                <bg-input
                  v-model="signup.password"
                  :error="errors && errors[0]"
                  :valid="changed && valid"
                  type="password"
                />
              </bg-field>
            </ValidationProvider>
            <ValidationProvider
              rules="required|password:confirmation"
              v-slot="{ errors, valid, changed }"
            >
              <bg-field
                :error="errors && errors[0]"
                label="Re-enter password"
                name="password"
              >
                <bg-input
                  :error="errors && errors[0]"
                  :valid="changed && valid"
                  v-model="signup.password2"
                  type="password"
                />
              </bg-field>
            </ValidationProvider>
            <bg-field>
              <bg-checkbox
                id="newsletter"
                v-model="isNewsletterChecked"
                label="Signup for our newsletter"
                @change="toggleNewsletter"
              />
            </bg-field>
            <bg-button
              class="signin-button"
              :isLoading="isSignupLoading"
              text="Sign up"
              type="submit"
            />
          </form>
        </div>
        <div class="disclaimer">
          By signing in or creating an account, you agree with our
          <a
            class="disclaimer-text"
            target="_blank"
            href="https://braingineers.com/wp-content/uploads/Data-Privacy-Policy.pdf"
            >Terms & Conditions and Privacy Statement</a
          >
        </div>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="signupValidationScreen">
        <p class="validation-sent" v-bg-typography="'m-body'">
          We've sent you an email containing a validation link, follow the link
          and you'll be able to sign in!
        </p>
      </div>
    </transition>
  </public-layout>
</template>

<style scoped>
.signup-button {
  display: inline;
}
.names {
  display: flex;
  justify-content: space-between;
}

.first_name {
  margin-right: 10px;
}

.last_name {
  margin-left: 10px;
}

.action-container {
  display: flex;
  margin-top: 20px;
}
.forgot-password {
  flex-grow: 1;
  align-self: center;
  text-align: center;
}
.forms-container {
  display: flex;
  margin-bottom: 30px;
}
.signin {
  width: 50%;
  padding: 40px;
}

.signup-help {
  margin: 8px 0 16px;
  color: var(--bg-color-text-light);
}

.signin-error {
  margin: 8px 0 12px;
  color: #d93025;
}

.validation-sent {
  text-align: center;
  margin-top: 30px;
}

.signin:first-child {
  border-right: 1px solid var(--bg-color-line);
}

.signin__header {
  text-align: center;
  margin-bottom: 40px;
}

.disclaimer {
  max-width: 400px;
  text-align: center;
  margin: auto;
  font-size: 16px;
  color: var(--bg-color-text-light);
}

.disclaimer-text {
  color: var(--bg-color-text-link);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-active {
  transition-delay: 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<script>
import PublicLayout from "@/layouts/public";
import BgTypography from "@/directives/bg-typography";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import BgButton from "@/components/bg-button";
import BgCheckbox from "@/components/bg-checkbox";
import API from "@/api";

export default {
  name: "signup",
  components: {
    BgButton,
    BgInput,
    BgField,
    BgCheckbox,
    PublicLayout,
  },
  directives: {
    BgTypography,
  },
  data: () => ({
    signupValidationScreen: false,
    isNewsletterChecked: false,
    isSigninLoading: false,
    isSignupLoading: false,
    signinError: "",
    signin: {
      password: "",
      email: "",
    },
    signup: {
      email: "",
      password: "",
      password2: "",
      profile: {
        first_name: "",
        last_name: "",
        newsletter: false,
      },
    },
  }),
  methods: {
    postSignin() {
      this.isSigninLoading = true;
      this.signinError = "";
      this.$refs.signin.$el.focus();
      this.$store
        .dispatch("login", this.signin)
        .then((res) => {
          if (!res || !res.id) {
            this.isSigninLoading = false;
            const resErrors = (res && (res.errors || res)) || {};
            if (resErrors.email || resErrors.password) {
              this.signinError = "The login information is incorrect.";
            }
            const contractErrors =
              (res && (res.contract || (res.errors && res.errors.contract))) ||
              null;
            if (contractErrors) {
              if (this.$refs["signin_email"])
                this.$refs["signin_email"].setErrors(contractErrors);
              this.signinError = Array.isArray(contractErrors)
                ? contractErrors.join(" ")
                : String(contractErrors);
            }
            return;
          }
          this.isSigninLoading = false;
          res.role === "demo"
            ? this.$router.push("/demo")
            : this.$router.push("/home");
        })
        .catch((error) => {
          this.isSigninLoading = false;
          // The login action throws err.response.data.errors directly, but it
          // may also reject with a raw axios error or an { errors: {...} } wrapper.
          const errors =
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.errors) ||
            (error && error.errors) ||
            error ||
            {};
          if (errors.email || errors.password) {
            this.signinError = "The login information is incorrect.";
          }
          if (errors.contract) {
            if (this.$refs["signin_email"])
              this.$refs["signin_email"].setErrors(errors.contract);
            this.signinError = Array.isArray(errors.contract)
              ? errors.contract.join(" ")
              : String(errors.contract);
          }
        });
    },
    postSignup() {
      if (this.signup.password !== this.signup.password2) {
        return null;
      }
      this.isSignupLoading = true;
      this.signup.profile.newsletter = this.isNewsletterChecked;
      API.user
        .signup(this.signup)
        .then(() => {
          this.isSignupLoading = false;
          this.signupValidationScreen = true;
        })
        .catch((error) => {
          const errors = error.response.data.errors;
          Object.keys(errors).map((e) => {
            if (this.$refs[e]) {
              this.$refs[e].setErrors(errors[e]);
            }
          });
          this.isSignupLoading = false;
        });
    },
    toggleNewsletter() {
      this.isNewsletterChecked = !this.isNewsletterChecked;
    },
  },
};
</script>
